// ─── Farm Adventure content pack — SPANISH (2026-08-12) ───
// First Spanish-language pack (separate Spanish track, approved by Natalya
// 2026-08-12 — "Spanish version of Farm" email thread). Source of truth:
// curriculum/Farm-Adventure-Spanish.pdf (Natalya's attachment, saved from
// Gmail 2026-08-12).
//
// Same 9 cards + deck algorithm + images as English Farm; everything the
// child HEARS and SAYS is Spanish. The engine stays language-agnostic: this
// pack supplies q()/sentence()/correctLine()/revealLine() string builders,
// universal `lines`, Spanish number words, STT/judge language config. Packs
// that omit these fields (farm, playground) keep the built-in English
// behavior — zero change to the live English app.
//
// Wording decisions (flag for Natalya's teacher review):
//   • Multi cards ask "¿Qué son estos/estas?" (the curriculum's Práctica
//     Hablada uses singular "¿Qué es esto?" throughout; we mirror the
//     English app's singular/plural split, with gender agreement).
//   • Pigs: display color is "rosados" (curriculum lists "rosa" — "un cerdo
//     rosa"; plural pedagogy reads better as "cerdos rosados"). BOTH forms
//     are accepted answers.
//   • Counting question drops the curriculum's "allá": "¿Cuántos caballos
//     hay?" — cleaner audio for isolated cards.

const QUESTIONS_PER_GAME = 10;

// Gendered helpers — Spanish agreement lives HERE, not in the engine.
const G = {
  f: { these: 'Estas', theseLower: 'estas', the: 'las', howMany: 'Cuántas' },
  m: { these: 'Estos', theseLower: 'estos', the: 'los', howMany: 'Cuántos' },
};

// Shared name-tier questions (audio pre-baked with the es- set).
const ASK_M = { audio: '/audio/es-what-are-these-m.mp3', text: '¿Qué son estos?' };
const ASK_F = { audio: '/audio/es-what-are-these-f.mp3', text: '¿Qué son estas?' };
const ASK_S = { audio: '/audio/es-what-is-this.mp3', text: '¿Qué es esto?' };

// `color` is the ACCEPT list (engine splits on " and "); colorWord is the
// display/teaching form and must be the FIRST accept entry (the judge's
// target word is accept[0]).
const pool = [
  { id: 'es-cow-multi', image: '/images/unit1/Two Black Cows.png', word: 'vaca', plural: 'vacas', gender: 'f',
    color: 'negras and negra and negros and negro', colorWord: 'negras', count: 2, numberWord: 'dos', type: 'animal', ask: ASK_F },
  { id: 'es-pig-multi', image: '/images/unit1/Two Pink Pigs.png', word: 'cerdo', plural: 'cerdos', gender: 'm',
    color: 'rosados and rosado and rosas and rosa', colorWord: 'rosados', count: 2, numberWord: 'dos', type: 'animal', ask: ASK_M,
    extraWords: ['chancho', 'chanchos', 'puerco', 'puercos', 'cochino', 'cochinos', 'marrano', 'marranos'] },
  { id: 'es-chicken-multi', image: '/images/unit1/Three Brown Chickens.png', word: 'gallina', plural: 'gallinas', gender: 'f',
    color: 'marrones and marrón and marron and café and cafés and cafe', colorWord: 'marrones', count: 3, numberWord: 'tres', type: 'bird', ask: ASK_F,
    extraWords: ['pollo', 'pollos'] },
  { id: 'es-horse-multi', image: '/images/unit1/Three Brown Horses.png', word: 'caballo', plural: 'caballos', gender: 'm',
    color: 'marrones and marrón and marron and café and cafés and cafe', colorWord: 'marrones', count: 3, numberWord: 'tres', type: 'animal', ask: ASK_M },
  { id: 'es-duck-multi', image: '/images/unit1/Four Brown Ducks.png', word: 'pato', plural: 'patos', gender: 'm',
    color: 'marrones and marrón and marron', colorWord: 'marrones', count: 4, numberWord: 'cuatro', type: 'bird', ask: ASK_M, skipColor: true },
  { id: 'es-sheep-multi', image: '/images/unit1/Four White Sheep.png', word: 'oveja', plural: 'ovejas', gender: 'f',
    color: 'blancas and blanca and blancos and blanco', colorWord: 'blancas', count: 4, numberWord: 'cuatro', type: 'animal', ask: ASK_F,
    extraWords: ['borrego', 'borregos'] },
  { id: 'es-goose', image: '/images/unit1/Geese.png', word: 'ganso', plural: 'gansos', gender: 'm',
    color: 'blancos and blanco and blancas and blanca', colorWord: 'blancos', count: 2, numberWord: 'dos', type: 'bird', ask: ASK_M },
  { id: 'es-rabbit', image: '/images/unit1/Rabbits.png', word: 'conejo', plural: 'conejos', gender: 'm',
    color: 'marrones and marrón and marron and café and cafés and cafe', colorWord: 'marrones', count: 2, numberWord: 'dos', type: 'animal', ask: ASK_M,
    extraWords: ['conejito', 'conejitos'] },
  // Name-only single (same rationale as English: turkey coloring too varied).
  { id: 'es-turkey', image: '/images/unit1/Turkey.png', word: 'pavo', plural: 'pavos', gender: 'm',
    color: 'marrones and marrón and marron', colorWord: 'marrones', count: 1, numberWord: 'uno', type: 'bird', nameOnly: true, ask: ASK_S,
    extraWords: ['guajolote'] },
];

// Deck algorithm: identical to English Farm (3 full-tier multis topped up
// with name-only cards to exactly 10 questions).
function buildDeck() {
  const turkey = pool.find((a) => a.id === 'es-turkey');
  const multiPool = pool.filter((a) => a.count > 1);
  const tiersFor = (card) => card.skipColor ? ['name', 'count'] : ['name', 'color', 'count'];

  const shuffledMulti = [...multiPool].sort(() => Math.random() - 0.5);
  const fullMultis = [];
  const usedWords = new Set();
  for (const m of shuffledMulti) {
    if (fullMultis.length >= 3) break;
    if (!usedWords.has(m.word)) {
      fullMultis.push({ ...m, tiers: tiersFor(m) });
      usedWords.add(m.word);
    }
  }
  const questionsUsed = fullMultis.reduce((sum, c) => sum + c.tiers.length, 0);
  const slotsRemaining = QUESTIONS_PER_GAME - questionsUsed;
  const remainingMultis = multiPool.filter((m) => !usedWords.has(m.word));
  const nameOnlyCandidates = [turkey, ...remainingMultis].sort(() => Math.random() - 0.5);
  const nameOnlyCards = nameOnlyCandidates
    .slice(0, slotsRemaining)
    .map((c) => ({ ...c, tiers: ['name'] }));
  return [...fullMultis, ...nameOnlyCards].sort(() => Math.random() - 0.5);
}

// ─── String builders (the engine prefers these when present) ───
const isMulti = (c) => c.count > 1;

function q(card, tier) {
  const g = G[card.gender];
  if (tier === 'name') return card.ask;
  if (tier === 'color') {
    return { audio: `/audio/${card.id}-color-ask.mp3`,
      text: isMulti(card) ? `¿De qué color son ${g.the} ${card.plural}?` : `¿De qué color es?` };
  }
  return { audio: `/audio/${card.id}-count-ask.mp3`,
    text: `¿${g.howMany} ${card.plural} hay?` };
}

function sentence(card, tier) {
  const g = G[card.gender];
  if (tier === 'name') {
    return isMulti(card)
      ? { sentence: `${g.these} son ${card.plural}.`, highlight: card.plural }
      : { sentence: `Esto es un ${card.word}.`, highlight: card.word };
  }
  if (tier === 'color') {
    return { sentence: `${g.these} ${card.plural} son ${card.colorWord}.`, highlight: card.colorWord };
  }
  return { sentence: `Hay ${card.numberWord} ${card.plural}.`, highlight: card.numberWord };
}

function correctLine(card, tier) {
  const s = sentence(card, tier).sentence;
  const suffix = tier === 'name' ? '' : tier === 'color' ? '-color' : '-count';
  return { audio: `/audio/${card.id}${suffix}-correct.mp3`, text: `¡Sí! ${s}` };
}

function revealLine(card, tier) {
  const s = sentence(card, tier).sentence;
  const suffix = tier === 'name' ? '' : tier === 'color' ? '-color' : '-count';
  return { audio: `/audio/${card.id}${suffix}-reveal.mp3`, text: `${s} Repite después de mí. ${s}` };
}

// ─── Universal frog lines (pre-baked with tts/gen-farm-es-audio.mjs) ───
const lines = {
  greeting: { audio: '/audio/es-greeting.mp3', text: '¡Hola! ¡Vamos a mirar las fotos!' },
  didntHear: { audio: '/audio/es-didnt-hear.mp3', text: 'No te escuché. ¡Inténtalo otra vez!' },
  tryAgain: { audio: '/audio/es-try-again.mp3', text: 'No, inténtalo otra vez.' },
  notQuite: { audio: '/audio/es-not-quite.mp3', text: 'Mmm, no exactamente. ¡Una vez más!' },
  soClose: { audio: '/audio/es-so-close.mp3', text: '¡Uy, casi casi! ¡Inténtalo otra vez!' },
  nowFullSentence: { audio: '/audio/es-now-full-sentence.mp3', text: '¡Sí! ¡Ahora di la oración completa!' },
  greatSentence: { audio: '/audio/es-great-sentence.mp3', text: '¡Maravilloso! ¡Dijiste la oración completa!' },
  bonusAll: { audio: '/audio/es-bonus-all.mp3', text: '¡Increíble! ¡Me dijiste todo sobre esta foto! ¡Te ganaste todas las estrellas!' },
  bonusExtra: { audio: '/audio/es-bonus-extra.mp3', text: '¡Guau! ¡Me dijiste más de lo que te pregunté! ¡Estrella extra!' },
  letsTryAnother: { audio: '/audio/es-lets-try-another.mp3', text: 'Vamos a intentar con otra.' },
  sayInTarget: { audio: '/audio/es-say-in-spanish.mp3', text: '¡Muy bien! Pero vamos a decirlo en español.' },
  completeGreat: { audio: '/audio/es-complete-great.mp3', text: '¡Muy buen trabajo! ¡Lo hiciste increíble hoy!' },
  completeGoodTry: { audio: '/audio/es-complete-good-try.mp3', text: '¡Buen intento! ¡Lo hiciste increíble hoy!' },
  favoriteWrapup: { audio: '/audio/es-favorite-wrapup.mp3', text: '¡Está bien! ¡Sigamos explorando!' },
};

// Feminine words for the favorite-celebration article (todo lo demás → "los").
const FEM_WORDS = new Set(['vaca', 'gallina', 'oveja', 'cabra', 'llama', 'alpaca', 'mula', 'yegua', 'tortuga']);

const plurals = pool.map((a) => a.plural).join(', ');

export default {
  id: 'farm-es',
  title: 'Aventura en la Granja',
  langId: 'es',
  pool,
  buildDeck,
  // ─── Language layer (engine falls back to English when these are absent) ───
  q, sentence, correctLine, revealLine, lines,
  numberWords: {
    1: ['uno', 'una', '1'], 2: ['dos', '2'], 3: ['tres', '3'], 4: ['cuatro', '4'],
    5: ['cinco', '5'], 6: ['seis', '6'], 7: ['siete', '7'], 8: ['ocho', '8'],
    9: ['nueve', '9'], 10: ['diez', '10'],
  },
  strictNumberWords: {
    1: ['uno', 'una', '1'], 2: ['dos', '2'], 3: ['tres', '3'], 4: ['cuatro', '4'],
    5: ['cinco', '5'], 6: ['seis', '6'], 7: ['siete', '7'], 8: ['ocho', '8'],
    9: ['nueve', '9'], 10: ['diez', '10'],
  },
  // Two-word answers that count as sentence frames ("es vaca", "son vacas",
  // "veo vacas"). Bare articles are deliberately EXCLUDED — "una vaca" is the
  // word, not the sentence; the ladder still teaches "Estas son vacas."
  linkers: ['es', 'son', 'esto', 'esta', 'estas', 'estos', 'hay', 'veo'],
  targetLanguage: 'Spanish',
  sttLang: 'es-US',
  ui: {
    tapToStart: '¡Toca para empezar!',
    youSaid: 'Dijiste:',
    playAgain: '¡Otra vez!',
    menu: 'Menú',
    greatJob: '¡Muy buen trabajo!',
    goodTry: '¡Buen intento!',
    stars: (s, t) => `¡Ganaste ${s} de ${t} estrellas!`,
    repeatAfterMe: 'Repite después de mí:',
    nowFullSentence: '¡Di la oración completa!',
    didntHear: '¡No te escuché!',
    letsTryAnother: 'Vamos a intentar con otra.',
    sayInTarget: '¡Vamos a decirlo en español!',
    wholeSentence: '¡Dijiste la oración completa!',
    micTap: '¡Toca para hablar!',
    micListening: 'Escuchando...',
  },
  hasFavorite: true,
  favorite: {
    question: '¿Cuál es tu animal favorito de la granja?',
    askAudio: '/audio/es-favorite-ask.mp3',
    image: '/images/unit1/All-Farm-Animals.jpg',
    judgeKind: 'farm animal',
    // The judge must map to SPANISH pool words for this pack.
    language: 'Spanish',
    audioFor: (word) => `/audio/es-favorite-${word}.mp3`,
    celebrateLine: (fav) => {
      const art = FEM_WORDS.has(fav.word) ? 'las' : 'los';
      return `¡A mí también me encantan ${art} ${fav.plural}! ¡Buena elección!`;
    },
    extras: [
      { word: 'cabra', plural: 'cabras' },
      { word: 'burro', plural: 'burros' },
      { word: 'toro', plural: 'toros' },
      { word: 'llama', plural: 'llamas' },
      { word: 'alpaca', plural: 'alpacas' },
      { word: 'poni', plural: 'ponis' },
      { word: 'mula', plural: 'mulas' },
      { word: 'gallo', plural: 'gallos' },
    ],
    synonyms: {
      conejito: 'conejo', pollito: 'gallina', ternero: 'vaca', vaquita: 'vaca',
      cordero: 'oveja', borrego: 'oveja', potro: 'caballo', caballito: 'caballo',
      cerdito: 'cerdo', chancho: 'cerdo', puerco: 'cerdo', marrano: 'cerdo',
      patito: 'pato', gansito: 'ganso', guajolote: 'pavo',
    },
    missKnown: { line: 'Ese no es un animal de la granja.', audio: '/audio/es-favorite-not-farm.mp3' },
    missUnknown: { line: 'Eso no es un animal.', audio: '/audio/es-favorite-not-animal.mp3' },
    hint: { line: 'Vamos a elegir juntos. ¿Qué tal una vaca o un caballo?', audio: '/audio/es-favorite-hint.mp3' },
  },
  // Spanish animals kids commonly name that are NOT farm animals — used by
  // the non-AI fallback to pick "not a farm animal" vs "not an animal".
  commonAnimals: [
    'perro', 'perrito', 'gato', 'gatito', 'león', 'tigre', 'elefante', 'jirafa',
    'cebra', 'mono', 'oso', 'lobo', 'zorro', 'venado', 'ardilla', 'ratón',
    'tortuga', 'rana', 'sapo', 'serpiente', 'lagarto', 'dinosaurio', 'unicornio',
    'pájaro', 'águila', 'búho', 'pingüino', 'pez', 'tiburón', 'ballena',
    'delfín', 'pulpo', 'mariposa', 'abeja', 'araña', 'hormiga',
  ],
  background: '/images/farm-background.webp',
  sttNameContext: `a Spanish farm-animal name, singular or plural (${plurals})`,
  sttColorContext: 'a Spanish color word (negro, blanco, marrón, rosado — any gender/number form)',
  sttCountContext: 'a Spanish number word from uno to diez',
  sttSentenceContext: 'a short spoken Spanish sentence about the picture',
  sttFavoriteContext: `a Spanish farm animal name (${plurals})`,
  judgeTopic: 'a farm picture card — the child is learning SPANISH and answers in Spanish: '
    + 'the animal name, its color, or how many there are',
  judgeHints: 'English: cow/cows=vaca/vacas, pig/pigs=cerdo/cerdos, chicken/hen=gallina, '
    + 'horse/horses=caballo/caballos, duck/ducks=pato/patos, sheep=oveja/ovejas, goose/geese=ganso/gansos, '
    + 'rabbit/bunny=conejo/conejos, turkey=pavo; brown=marrón/marrones, black=negro/negras, '
    + 'white=blanco/blancas, pink=rosado/rosados; two=dos, three=tres, four=cuatro. '
    + 'The TARGET language is Spanish — a correct-meaning ENGLISH answer is inOtherLanguage.',
};
